import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "../../components/header";
import Footer from "../../components/footer";
import SeoHead from "../../components/seoHead";
import { LocalizedLink, useAppLocale } from "../../i18n/LocaleRouting";
import { localizeProductEntry } from "../../i18n/productCopy";
import { SITE_NAME, SITE_URL, absoluteLocalizedUrl } from "../../i18n/seoUrls";
import { isFirebaseConfigured } from "../../lib/firebase";
import { formatRelativeTime } from "../../services/communityService";
import {
  NEWS_FEED_BRAND_LABELS,
  NEWS_FEED_BRAND_ORDER,
  isNewsFeedBrandKey,
  newsItemPath,
  subscribeNewsFeed,
  type NewsFeedBrandKey,
  type NewsFeedItem,
} from "../../services/newsFeedService";
import {
  PRODUCT_INFORMATION_LIST,
  type ProductInformationEntry,
} from "../productInformation/productInformationData";

type HeroLayout = "left" | "right" | "stack";

function heroLayoutForIndex(index: number): HeroLayout {
  const layouts: HeroLayout[] = ["left", "right", "stack"];
  return layouts[index % 3];
}

function HeroCopy({
  item,
  subtitle,
  align,
}: {
  item: ProductInformationEntry;
  subtitle: string;
  align: "left" | "right" | "center";
}) {
  const alignClass =
    align === "center"
      ? "items-center text-center"
      : align === "right"
        ? "items-end text-right"
        : "items-start text-left";

  return (
    <div
      className={`flex flex-col justify-center gap-1.5 md:gap-2 ${alignClass}`}
    >
      <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-slate-700">
        {item.h1Line1}
      </p>
      <h2 className="text-lg md:text-2xl font-extrabold leading-tight text-[#111]">
        {item.h1Accent}
      </h2>
      {subtitle ? (
        <p className="max-w-[95%] text-xs leading-snug text-slate-700">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function PreviewImage({
  src,
  className = "",
}: {
  src: string;
  className?: string;
}) {
  return (
    <img
      src={src}
      alt=""
      className={`rounded-md object-cover drop-shadow-lg select-none [-webkit-user-drag:none] ${className}`}
      loading="lazy"
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
    />
  );
}

function OfficialFeedImages({ urls }: { urls: string[] }) {
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const visible = urls.filter((src) => !failed.has(src)).slice(0, 4);
  if (visible.length === 0) {
    return <div className="mt-3 min-h-[140px] flex-1" aria-hidden />;
  }

  const single = visible.length === 1;

  return (
    <div
      className={`mt-3 min-h-[80px] flex-1 overflow-hidden rounded-lg bg-[#f7f7f7]/0 ${
        single
          ? "flex items-center justify-center"
          : "grid grid-cols-2 gap-1.5 p-1.5"
      }`}
    >
      {visible.map((src) => (
        <img
          key={src}
          src={src}
          alt=""
          className={`select-none [-webkit-user-drag:none] ${
            single
              ? "max-h-full max-w-full object-contain"
              : "h-full w-full rounded-md object-contain"
          }`}
          loading="lazy"
          referrerPolicy="no-referrer"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onError={() => {
            setFailed((prev) => {
              const next = new Set(prev);
              next.add(src);
              return next;
            });
          }}
        />
      ))}
    </div>
  );
}

function OfficialFeedCard({ item }: { item: NewsFeedItem }) {
  const { t } = useTranslation();
  const brandLabel = t(`news.brands.${item.brand}`, {
    defaultValue:
      NEWS_FEED_BRAND_LABELS[item.brand] ||
      item.displayName ||
      item.sourceAccount,
  });
  const body = item.text || item.title;
  const href = newsItemPath(item);

  return (
    <LocalizedLink
      to={href}
      className="flex h-[300px] flex-col overflow-hidden rounded-xl border border-[#e5e5e5] bg-white p-4 shadow-sm transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F05A2A] md:h-[360px] md:p-5"
    >
      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
        <span className="rounded-full border border-[#d4d4d4] bg-[#f3f3f3] px-1.5 py-0.5 text-[11px] text-[#555]">
          {brandLabel}
        </span>
        {item.sourceAccount ? (
          <span className="text-[11px] text-[#888]">@{item.sourceAccount}</span>
        ) : null}
        <time className="ml-auto text-xs text-[#888]">
          {formatRelativeTime(item.createdAt)}
        </time>
      </div>
      {body ? (
        <p className="shrink-0 whitespace-pre-wrap text-sm leading-relaxed text-[#222]">
          {body}
        </p>
      ) : null}
      <OfficialFeedImages urls={item.imageUrls} />
    </LocalizedLink>
  );
}

type NewsTab = "releases" | "official";

const News: React.FC = () => {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const newsUrl = absoluteLocalizedUrl("/news", locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const items = useMemo(
    () =>
      [...PRODUCT_INFORMATION_LIST]
        .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
        .map((entry) => localizeProductEntry(entry, locale)),
    [locale],
  );

  const newsTabs: { id: NewsTab; label: string }[] = [
    { id: "releases", label: t("news.tabs.releases") },
    { id: "official", label: t("news.tabs.official") },
  ];

  const [activeTab, setActiveTab] = useState<NewsTab>("releases");
  const [selectedBrand, setSelectedBrand] =
    useState<NewsFeedBrandKey>("pokemon");
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedRequested, setFeedRequested] = useState(false);
  const [feedItems, setFeedItems] = useState<NewsFeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get("tab") !== "official") return;

    const brandParam = searchParams.get("brand");
    const brand = isNewsFeedBrandKey(brandParam) ? brandParam : "pokemon";

    setActiveTab("official");
    setSelectedBrand(brand);
    setFeedRequested(true);

    // Keep brand in the query for back-navigation; canonical SEO stays on /news.
    if (brandParam !== brand) {
      setSearchParams(
        { tab: "official", brand },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!feedRequested || !isFirebaseConfigured) return;

    setFeedLoading(true);
    const unsub = subscribeNewsFeed(
      (next) => {
        setFeedItems(next);
        setFeedError(null);
        setFeedLoading(false);
      },
      (error) => {
        setFeedError(error.message || "Could not load official updates.");
        setFeedLoading(false);
      },
    );
    return () => unsub();
  }, [feedRequested]);

  const filteredFeedItems = useMemo(
    () => feedItems.filter((item) => item.brand === selectedBrand),
    [feedItems, selectedBrand],
  );

  const selectedBrandLabel = t(`news.brands.${selectedBrand}`, {
    defaultValue: NEWS_FEED_BRAND_LABELS[selectedBrand] || selectedBrand,
  });

  const openOfficial = (brand: NewsFeedBrandKey = selectedBrand) => {
    setActiveTab("official");
    setSelectedBrand(brand);
    setSearchParams({ tab: "official", brand });
    if (!feedRequested) {
      setFeedRequested(true);
      if (isFirebaseConfigured) setFeedLoading(true);
    }
  };

  const selectTab = (tab: NewsTab) => {
    if (tab === "official") {
      openOfficial(selectedBrand);
      return;
    }
    setActiveTab("releases");
    setSearchParams({});
  };

  const selectBrand = (brand: NewsFeedBrandKey) => {
    openOfficial(brand);
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <SeoHead
        title={t("news.seoTitle")}
        description={t("news.seoDescription")}
        path="/news"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t("news.seoTitle"),
            description: t("news.seoDescription"),
            url: newsUrl,
            isPartOf: {
              "@type": "WebSite",
              name: SITE_NAME,
              url: SITE_URL + "/",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("common.home"),
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("news.title"),
                item: newsUrl,
              },
            ],
          },
        ]}
      />
      <Header />

      <main className="w-full max-w-[1050px] mx-auto px-8 py-6 md:py-10">
        <h1 className="text-[24px] md:text-[28px] font-bold text-[#111] mb-4">
          {t("news.title")}
        </h1>

        <div
          role="tablist"
          aria-label="News sections"
          className="mb-6 flex gap-6 border-b border-[#e5e5e5]"
        >
          {newsTabs.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                id={`news-tab-${tab.id}`}
                onClick={() => selectTab(tab.id)}
                className={`-mb-px border-b-2 pb-2.5 text-base md:text-lg font-medium transition-colors ${
                  selected
                    ? "border-[#F05A2A] text-[#F05A2A]"
                    : "border-transparent text-[#888] hover:text-[#555]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "official" ? (
          <section
            role="tabpanel"
            aria-labelledby="news-tab-official"
            className="mb-4"
          >
            <div
              role="tablist"
              aria-label={t("news.brandFilterAria")}
              className="mb-5 flex flex-wrap gap-2"
            >
              {NEWS_FEED_BRAND_ORDER.map((brand) => {
                const selected = selectedBrand === brand;
                return (
                  <button
                    key={brand}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => selectBrand(brand)}
                    className={`rounded-full px-2 py-[2px] text-sm transition-colors ${
                      selected
                        ? "border border-[#F05A2A] font-medium text-[#F05A2A]"
                        : "text-[#888] hover:text-[#F05A2A]"
                    }`}
                  >
                    {t(`news.brands.${brand}`, {
                      defaultValue: NEWS_FEED_BRAND_LABELS[brand],
                    })}
                  </button>
                );
              })}
            </div>

            {!isFirebaseConfigured ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Firebase is not configured, so official posts cannot load yet.
              </p>
            ) : feedLoading ? (
              <p className="text-sm text-[#888]">{t("common.loading")}</p>
            ) : feedError ? (
              <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {feedError}
              </p>
            ) : feedItems.length === 0 ? (
              <p className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-3 text-sm text-[#666]">
                {t("news.officialEmpty")}
              </p>
            ) : filteredFeedItems.length === 0 ? (
              <p className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-3 text-sm text-[#666]">
                {t("news.officialBrandEmpty", { brand: selectedBrandLabel })}
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {filteredFeedItems.map((item) => (
                  <li key={item.id}>
                    <OfficialFeedCard item={item} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <section
            role="tabpanel"
            aria-labelledby="news-tab-releases"
            className="max-w-[720px] mx-auto"
          >
            <ul className="flex flex-col gap-5">
              {items.map((item, index) => {
                const layout = heroLayoutForIndex(index);
                const previewImages = item.images.slice(0, 3);
                const hasImages = previewImages.length > 0;
                const subtitle = [item.subtitleLine1, item.subtitleLine2]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <li key={item.id}>
                    <LocalizedLink
                      to={`/${item.brand}/${item.slug}`}
                      className="block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F05A2A]"
                    >
                      <div className="relative min-h-[200px] md:min-h-[240px] overflow-hidden">
                        <div
                          className="absolute inset-0 scale-105 bg-cover bg-center"
                          style={{
                            backgroundImage: `url('${item.heroImageUrl}')`,
                          }}
                          aria-hidden
                        />
                        <div
                          className="absolute inset-0 bg-white/55 backdrop-blur-[6px] backdrop-saturate-[1.1]"
                          aria-hidden
                        />

                        <div className="relative z-[1] p-4 md:p-5">
                          {!hasImages ? (
                            <div className="flex min-h-[168px] md:min-h-[200px] items-center">
                              <HeroCopy
                                item={item}
                                subtitle={subtitle}
                                align="left"
                              />
                            </div>
                          ) : layout === "stack" ? (
                            <div className="flex min-h-[200px] md:min-h-[240px] flex-col items-center justify-center gap-4">
                              <HeroCopy
                                item={item}
                                subtitle={subtitle}
                                align="center"
                              />
                              <div className="flex w-full items-end justify-center gap-2 md:gap-3">
                                {previewImages.map((src) => (
                                  <PreviewImage
                                    key={src}
                                    src={src}
                                    className="h-28 w-auto md:h-32"
                                  />
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`flex min-h-[168px] md:min-h-[200px] items-stretch gap-3 md:gap-4 ${
                                layout === "right"
                                  ? "flex-row-reverse"
                                  : "flex-row"
                              }`}
                            >
                              <div className="flex w-[38%] max-w-[160px] shrink-0 items-center justify-center md:max-w-[180px]">
                                <PreviewImage
                                  src={previewImages[0]}
                                  className={`w-full ${
                                    layout === "left" ? "-rotate-3" : "rotate-3"
                                  }`}
                                />
                              </div>
                              <div className="flex flex-1 items-center">
                                <HeroCopy
                                  item={item}
                                  subtitle={subtitle}
                                  align={layout === "left" ? "right" : "left"}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-2 flex items-center gap-2.5">
                          <span className="rounded-full border border-[#d4d4d4] bg-[#f3f3f3] px-1.5 py-0.5 text-[11px] text-[#555]">
                            {t("news.tag.release")}
                          </span>
                          <time
                            dateTime={item.releaseDate}
                            className="text-xs text-[#888]"
                          >
                            {item.releaseDate}
                          </time>
                        </div>
                        <h3 className="mb-1.5 text-base md:text-lg font-bold text-[#111] leading-snug">
                          {item.sectionTitle}
                        </h3>
                        <p className="text-xs text-[#666] leading-relaxed line-clamp-3">
                          {item.sectionBody}
                        </p>
                      </div>
                    </LocalizedLink>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default News;
